import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  onSnapshot,
  setDoc,
  updateDoc,
} from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { db, functions } from "@/lib/firebase";
import { getAppConfig } from "@/lib/shiftConfig";
import { getShortDayName, getCurrentOrLastMonday, getWeekDates } from "@/lib/format";
import { Toast } from "@/components/Toast";
import { OvertimeInput } from "@/components/OvertimeInput";
import { PayrollPage } from "./PayrollPage";
import type { AdjustmentCell, AppConfig, Employee, Registration, WeekDoc, WeekStatus } from "@/lib/types";

/** Shorten "Lê Quốc Dương" → "L.Q.Dương" */
function shortenName(full: string): string {
  const parts = full.trim().split(/\s+/);
  if (parts.length < 2) return full;
  const last = parts.pop()!;
  const initials = parts.map((p) => p[0].toUpperCase()).join(".");
  return `${initials}.${last}`;
}

type Tab = "registrations" | "approval" | "payroll";

export function ScheduleReview() {
  const { weekId } = useParams<{ weekId: string }>();
  const [week, setWeek] = useState<WeekDoc | null>(null);
  const [config, setConfig] = useState<AppConfig | null>(null);
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [registrations, setRegistrations] = useState<Registration[]>([]);
  const [tab, setTab] = useState<Tab>("registrations");
  const [toast, setToast] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  // Real-time official schedule (filled by toggleName clicks)
  const [officialSchedule, setOfficialSchedule] = useState<Record<string, Record<string, string[]>>>({});
  // Real-time overtime / early-leave adjustments, keyed date → shift → phone
  const [adjustments, setAdjustments] = useState<Record<string, AdjustmentCell>>({});
  const [publishUrl, setPublishUrl] = useState<string | null>(null);

  useEffect(() => {
    if (!weekId) return;
    (async () => {
      const [cfg, weekSnap, empSnap] = await Promise.all([
        getAppConfig(),
        getDoc(doc(db, "weeks", weekId)),
        getDocs(collection(db, "employees")),
      ]);
      setConfig(cfg);
      if (weekSnap.exists()) {
        const d = weekSnap.data();
        setWeek({
          label: d.label as string,
          startDate: d.startDate as string,
          endDate: d.endDate as string,
          status: d.status as WeekStatus,
          createdAt: 0,
        });
        if (typeof d.publishUrl === "string") setPublishUrl(d.publishUrl);
      }
      setEmployees(
        empSnap.docs
          .map((d) => d.data() as Employee)
          .filter((e) => e.active)
          .sort((a, b) => a.name.localeCompare(b.name, "vi")),
      );
    })();
  }, [weekId]);

  // Real-time registrations
  useEffect(() => {
    if (!weekId) return;
    return onSnapshot(collection(db, "weeks", weekId, "registrations"), (snap) => {
      setRegistrations(
        snap.docs.map((d) => {
          const data = d.data();
          return {
            phone: data.phone as string,
            name: data.name as string,
            updatedAt: 0,
            shifts: (data.shifts as Registration["shifts"]) ?? [],
          };
        }),
      );
    });
  }, [weekId]);

  // Real-time official schedule (for approval tab)
  useEffect(() => {
    if (!weekId) return;
    return onSnapshot(collection(db, "weeks", weekId, "officialSchedule"), (snap) => {
      const schedule: Record<string, Record<string, string[]>> = {};
      snap.docs.forEach((d) => {
        schedule[d.id] = d.data() as Record<string, string[]>;
      });
      setOfficialSchedule(schedule);
    });
  }, [weekId]);

  // Real-time overtime / early-leave adjustments (for approval tab)
  useEffect(() => {
    if (!weekId) return;
    return onSnapshot(collection(db, "weeks", weekId, "adjustments"), (snap) => {
      const next: Record<string, AdjustmentCell> = {};
      snap.docs.forEach((d) => {
        next[d.id] = d.data() as AdjustmentCell;
      });
      setAdjustments(next);
    });
  }, [weekId]);

  const monday = useMemo(() => {
    if (week) return new Date(week.startDate + "T00:00:00");
    return getCurrentOrLastMonday();
  }, [week]);
  const dates = useMemo(() => getWeekDates(monday), [monday]);

  // Count how many shifts each employee has been approved for in officialSchedule.
  const approvedStatsByEmployee = useMemo(() => {
    if (!config) return new Map<string, { count: number; totalHours: number }>();
    const counts = new Map<string, { count: number; totalHours: number }>();
    for (const date in officialSchedule) {
      for (const shiftLabel in officialSchedule[date]) {
        const hours = config.shifts.find((s) => s.label === shiftLabel)?.hours ?? 0;
        for (const name of officialSchedule[date][shiftLabel]) {
          const prev = counts.get(name) ?? { count: 0, totalHours: 0 };
          counts.set(name, { count: prev.count + 1, totalHours: prev.totalHours + hours });
        }
      }
    }
    return counts;
  }, [officialSchedule, config]);

  const unapprove = async () => {
    if (!week || week.status !== "approved" || saving || !weekId) return;
    if (!confirm("Hủy duyệt? Lịch đã chọn vẫn giữ nguyên để anh chỉnh sửa.")) return;
    setSaving(true);
    try {
      await updateDoc(doc(db, "weeks", weekId), { status: "open" } as Partial<WeekDoc>);
      setWeek({ ...week, status: "open" });
      setToast("✅ Đã hủy duyệt — lịch giữ nguyên, tuần đang mở lại.");
    } finally {
      setSaving(false);
    }
  };

  if (!week || !config || !weekId) {
    return <div className="p-8 text-center text-gray-500">Đang tải tuần…</div>;
  }

  const isWeekendDate = (dateStr: string): boolean => {
    const d = new Date(dateStr + "T00:00:00");
    return d.getDay() === 0 || d.getDay() === 6;
  };

  // Helper: get required headcount for a given (shift, date) based on staffing config
  const getRequired = (shiftLabel: string, dateStr: string): number => {
    if (!config?.staffing) return 1;
    const rule = config.staffing[shiftLabel];
    if (!rule) return 1;
    return isWeekendDate(dateStr) ? rule.weekend : rule.weekday;
  };

  // For each (date × shift) cell, compute the list of names from registrations.
  const cellContent = (date: string, shiftLabel: string): { names: string[]; state: 'empty' | 'ok' | 'short' } => {
    const names: string[] = [];
    registrations.forEach((reg) => {
      reg.shifts.forEach((s) => {
        if (s.date === date && s.shift === shiftLabel) names.push(reg.name);
      });
    });
    const req = getRequired(shiftLabel, date);
    const state = names.length === 0 ? 'empty' : names.length >= req ? 'ok' : 'short';
    return { names, state };
  };

  // For approval tab: use officialSchedule data (updated in real-time by toggleName)
  const approvalCellContent = (date: string, shiftLabel: string): string[] => {
    return officialSchedule[date]?.[shiftLabel] ?? [];
  };

  const toggleName = async (date: string, shiftLabel: string, name: string) => {
    if (week.status !== "open" || saving) return;
    setSaving(true);
    try {
      const ref = doc(db, "weeks", weekId, "officialSchedule", date);
      const snap = await getDoc(ref);
      const current: Record<string, string[]> = snap.exists()
        ? (snap.data() as Record<string, string[]>)
        : { "7:30-12:00": [], "12:00-16:00": [], "16:00-22:00": [] };
      const list = current[shiftLabel] ?? [];
      const next = list.includes(name) ? list.filter((n) => n !== name) : [...list, name];
      await setDoc(ref, { ...current, [shiftLabel]: next }, { merge: true });
    } finally {
      setSaving(false);
    }
  };

  // Persist an overtime / early-leave adjustment for one (date, shift, phone)
  // immediately — so it survives reload, independent of the approve action.
  const updateAdjustment = async (
    date: string,
    shiftLabel: string,
    phone: string,
    overtimeHours: number,
    earlyLeaveHours: number,
  ) => {
    if (week.status === "paid") return;
    const ref = doc(db, "weeks", weekId, "adjustments", date);
    const snap = await getDoc(ref);
    const current = (snap.exists() ? (snap.data() as AdjustmentCell) : {}) as AdjustmentCell;
    const cell = { ...(current[shiftLabel] ?? {}) };
    if (overtimeHours === 0 && earlyLeaveHours === 0) {
      delete cell[phone];
    } else {
      cell[phone] = { overtimeHours, earlyLeaveHours };
    }
    // Full-doc replace (we hold the complete doc) so removals take effect —
    // a merge write can never delete a phone key.
    await setDoc(ref, { ...current, [shiftLabel]: cell });
  };

  const approve = async () => {
    if (saving || week.status !== "open") return;
    setSaving(true);
    try {
      // officialSchedule already holds exactly the manager's selections, written in
      // real time by toggleName(). Approving only finalizes those choices — employee
      // registrations are reference-only input and must NOT be merged into the
      // official schedule.
      await updateDoc(doc(db, "weeks", weekId), { status: "approved" } as Partial<WeekDoc>);
      setWeek({ ...week, status: "approved" });

      // Auto-publish for sharing
      try {
        const fn = httpsCallable(functions, "publishWeek");
        const result = await fn({ weekId });
        const data = result.data as { publishUrl?: string };
        if (data?.publishUrl) setPublishUrl(data.publishUrl);
      } catch (e) {
        console.error("publishWeek failed:", e);
      }

      setToast("✅ Đã duyệt lịch tuần này");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mx-auto max-w-5xl px-4 pb-32 pt-4">
      <div className="mb-4 flex items-center justify-between">
        <h1 className="font-display text-2xl font-black text-gray-900">Tuần {week.label}</h1>
        <span className="rounded-full bg-pink-50 px-3 py-1 text-xs font-semibold text-pink-600">
          {week.status}
        </span>
      </div>

      <div className="sticky top-16 z-30 -mx-4 mb-3 flex gap-1 border-b border-pink-100 bg-white px-4 pt-3">
        {(["registrations", "approval", "payroll"] as Tab[]).map((t) => {
          const label = t === "registrations" ? "Đăng ký" : t === "approval" ? "Duyệt" : "Payroll";
          return (
            <button
              key={t}
              onClick={() => setTab(t)}
              className={`rounded-t-xl px-4 py-2 text-sm font-semibold transition-colors duration-200 ${
                tab === t
                  ? "bg-pink-500 text-white"
                  : "text-gray-500 hover:bg-pink-50 hover:text-pink-500"
              }`}
            >
              {label}
            </button>
          );
        })}
      </div>

      {tab === "payroll" ? (
        <PayrollPage weekId={weekId} hourlyRate={config.hourlyRate} />
      ) : (
        <>
          {/* Per-employee shift summary in approval tab */}
          {tab === "approval" && approvedStatsByEmployee.size > 0 && (
            <div className="mb-4 rounded-2xl border border-blue-100 bg-blue-50/50 p-4">
              <div className="mb-2 text-xs font-bold uppercase tracking-widest text-blue-600">
                📊 Thống kê nhân viên đã duyệt
              </div>
              <div className="flex flex-wrap gap-2">
                {Array.from(approvedStatsByEmployee.entries())
                  .sort((a, b) => b[1].totalHours - a[1].totalHours)
                  .map(([name, stats]) => (
                    <span
                      key={name}
                      className="inline-flex items-center gap-1.5 rounded-full border border-blue-200 bg-white px-3 py-1.5 text-xs font-medium text-blue-700"
                    >
                      <span className="font-bold">{name}</span>
                      <span className="text-blue-400">·</span>
                      <span>{stats.count} ca</span>
                      <span className="text-blue-400">·</span>
                      <span>{stats.totalHours}h</span>
                    </span>
                  ))}
              </div>
            </div>
          )}

          <div className="overflow-x-auto rounded-3xl bg-white shadow-lg ring-1 ring-pink-100">
            <table className="min-w-[640px] w-full text-sm">
              <thead className="bg-pink-50/50 text-xs font-bold uppercase tracking-wider text-gray-600">
                <tr>
                  <th className="p-3 text-left">Ca / Ngày</th>
                  {dates.map((d, i) => (
                    <th key={d} className="p-3 text-center">
                      <div>{getShortDayName(monday, i)}</div>
                      <div className="text-[10px] font-normal text-gray-400">{d.slice(5)}</div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {config.shifts.map((shift) => (
                  <tr key={shift.label} className="border-t border-pink-50">
                    <td className="p-3 align-top text-xs">
                      <div className="font-bold text-gray-800">{shift.label}</div>
                      {config?.staffing?.[shift.label] && (
                        <div className="text-[10px] text-gray-400">
                          T2-T6: {config.staffing[shift.label].weekday} · T7-CN: {config.staffing[shift.label].weekend}
                        </div>
                      )}
                    </td>
                    {dates.map((date) => {
                      if (shift.days && shift.days !== (isWeekendDate(date) ? "weekend" : "weekday")) {
                        return (
                          <td
                            key={`${date}-${shift.label}`}
                            className="min-w-[90px] border border-pink-50 bg-gray-50 p-2 align-top"
                          />
                        );
                      }
                      const { names, state } = cellContent(date, shift.label);
                      const bg =
                        state === "ok" ? "bg-green-50" : state === "short" ? "bg-rose-50" : "bg-white";
                      return (
                        <td
                          key={`${date}-${shift.label}`}
                          className={`min-w-[90px] border border-pink-50 p-2 align-top ${bg}`}
                        >
                          <div className="flex flex-col gap-1">
                            {tab === "approval"
                              ? employees.map((emp) => {
                                    const registered = names.includes(emp.name);
                                    const approved = approvalCellContent(date, shift.label).includes(emp.name);
                                    const adj = adjustments[date]?.[shift.label]?.[emp.phone];
                                    const ot = adj?.overtimeHours ?? 0;
                                    const vs = adj?.earlyLeaveHours ?? 0;
                                    return (
                                      <div key={emp.phone} className="flex flex-col gap-0.5">
                                        <button
                                          onClick={() => toggleName(date, shift.label, emp.name)}
                                          className={`rounded-full border px-2 py-0.5 text-left text-[11px] font-medium transition ${
                                            approved
                                              ? "border-pink-500 bg-pink-50 text-pink-600"
                                              : registered
                                                ? "border-emerald-400 bg-emerald-50 text-emerald-700"
                                                : "border-gray-200 bg-white text-gray-400 hover:bg-gray-50 hover:text-gray-600"
                                          }`}
                                        >
                                          {approved ? "✓ " : ""}
                                          {shortenName(emp.name)}
                                        </button>
                                        {approved && week.status !== "paid" && (
                                          <OvertimeInput
                                            key={`adj-${ot}-${vs}`}
                                            initialOvertime={ot}
                                            initialEarlyLeave={vs}
                                            onCommit={(o, l) => updateAdjustment(date, shift.label, emp.phone, o, l)}
                                          />
                                        )}
                                      </div>
                                    );
                                  })
                              : names.length === 0
                                ? <span className="text-[10px] text-gray-400">—</span>
                                : names.map((n) => (
                                    <span
                                      key={n}
                                      className="inline-flex w-fit items-center rounded-full border border-pink-200 bg-pink-50 px-2 py-0.5 text-[11px] font-medium text-pink-600"
                                    >
                                      {shortenName(n)}
                                    </span>
                                  ))
                            }
                          </div>
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {tab === "approval" && week.status === "open" && (
        <button
          onClick={approve}
          disabled={saving}
          className="btn-cta fixed bottom-6 right-6 z-30 inline-flex items-center gap-2 px-6 py-3 shadow-xl shadow-pink-200/50"
        >
          {saving ? "Đang duyệt…" : "✅ Duyệt lịch tuần này"}
        </button>
      )}

      {tab === "approval" && week.status === "approved" && (
        <button
          onClick={unapprove}
          disabled={saving}
          className="fixed bottom-6 left-6 z-30 inline-flex items-center gap-2 rounded-2xl bg-rose-500 px-5 py-3 text-sm font-semibold text-white shadow-xl shadow-rose-200/50 transition hover:bg-rose-600 disabled:opacity-50"
        >
          {saving ? "Đang hủy…" : "↩ Hủy duyệt"}
        </button>
      )}

      {/* Share link */}
      {tab === "approval" && week.status === "approved" && publishUrl && (
        <div className="fixed bottom-6 left-1/2 z-30 -translate-x-1/2 rounded-2xl border border-amber-200 bg-amber-50 px-5 py-3 shadow-lg shadow-amber-100/50">
          <div className="flex items-center gap-3">
            <span className="text-sm font-semibold text-amber-700">🔗 Link xem lịch:</span>
            <input
              readOnly
              value={publishUrl}
              className="min-w-[280px] rounded-lg border border-amber-200 bg-white px-3 py-1.5 text-xs text-gray-700"
              onFocus={(e) => e.target.select()}
            />
            <a
              href={publishUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="rounded-lg bg-amber-500 px-3 py-1.5 text-xs font-semibold text-white hover:bg-amber-600 transition"
            >
              Mở ↗
            </a>
          </div>
        </div>
      )}

      {toast && <Toast message={toast} onDismiss={() => setToast(null)} />}
    </div>
  );
}
